import { type Dayjs, dayjs } from '@vartion/ui'

import type { SyncCache } from '~/types.ts'
import { type CacheItem, createInstance, createKey, createValue, getValue, isExpired } from './helpers.ts'

export class InMemoryCache implements SyncCache {
  name: string
  cache: Record<string, string>

  constructor({ name }: { name: string }) {
    this.name = name
    this.cache = {}
    setInterval(this.flushExpired.bind(this), 60000)
  }

  static tags(tags: string | string[]): InMemoryCache {
    return createInstance('InMemoryCache', tags, InMemoryCache) as InMemoryCache
  }

  has(key: unknown) {
    return this.get(key) !== null
  }

  put(key: unknown, value: unknown, date = dayjs().add(1, 'year')) {
    this.cache[createKey(key)] = createValue(value, date)
  }

  get<T = any, D = null>(key: unknown, defaultValue: D = null as D): T | D {
    const cacheKey = createKey(key)

    const itemString = this.cache[cacheKey]
    if (!itemString) return defaultValue

    const item = itemString.split(':') as CacheItem

    if (isExpired(item)) {
      delete this.cache[cacheKey]

      return defaultValue
    }

    return getValue(item) as T
  }

  async remember<T = any>(key: unknown, duration: Dayjs, callback: () => T | Promise<T>): Promise<T> {
    let value = this.get(key)

    if (value !== null) {
      return value
    }

    value = await callback()

    this.put(key, value, duration)

    return value
  }

  forget(key: unknown): void {
    delete this.cache[createKey(key)]
  }

  list() {
    return Object.assign({}, this.cache)
  }

  flush() {
    this.cache = {}
  }

  flushExpired() {
    for (const key in this.cache) {
      const itemString = this.cache[key]
      if (!itemString) continue

      const item = itemString.split(':') as CacheItem

      if (isExpired(item)) {
        delete this.cache[key]
      }
    }
  }
}
